/**
 * OCSF severity, as something a screen can draw.
 *
 * Two defects this fixes, and the second is the one that matters.
 *
 * `SEVERITY_NAMES` in `Async.tsx` names ids 0 to 6, and `styles.css` defines `.pill.sev-1` through
 * `.sev-5`. So Unknown and Fatal have always rendered with no color at all - the two ends of the
 * scale, one of which is the worst thing the product can report.
 *
 * And in Broadsheet the palette has two inks, so `--severity-2`, `--severity-3` and `--success` are
 * all the same cyan: Low, Medium and success are **not distinguishable by color** there. That is the
 * deliberate cost of a two-ink system, and it means the uppercase label beside a severity bar is not
 * decoration - it is the only thing carrying the distinction. A severity rendered as color alone is
 * a severity an operator cannot read in the palette this console now ships with by default, and it is
 * unreadable to a color-blind operator in every palette. Do not tidy the label away.
 */
export interface SeverityLook {
  /** The class the pill and the bar take. Always defined, for every id. */
  className: string
  /** The word. Never omitted: in a two-ink palette it is the only thing distinguishing two levels. */
  label: string
  /** Whether this is a level somebody has to act on, for the ribbon and the bar lists. */
  urgent: boolean
}

const NAMES: Record<number, string> = {
  0: 'Unknown',
  1: 'Informational',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
  6: 'Fatal'
}

/** High and above. The threshold the Board paints in danger, and it comes from here rather than rank. */
export const URGENT_FROM = 4

export function severityLook(severityId: number): SeverityLook {
  // Clamped rather than defaulted, so a severity from a newer agent renders as the nearest level this
  // console understands instead of falling through to an unstyled pill. An unrecognized id above the
  // scale is more likely to be worse than 5 than better than 1.
  const clamped = Number.isFinite(severityId) ? Math.min(Math.max(Math.trunc(severityId), 0), 6) : 0

  return {
    className: `sev-${clamped}`,
    label: NAMES[clamped] ?? `Severity ${clamped}`,
    urgent: clamped >= URGENT_FROM
  }
}

/** The name alone, for a table cell that has its own color treatment. */
export function severityLabel(severityId: number): string {
  return severityLook(severityId).label
}

/** Every id this console draws, so the stylesheet can be asserted to cover all of them. */
export const SEVERITY_IDS: readonly number[] = [0, 1, 2, 3, 4, 5, 6]

/**
 * The severity words the alert API uses, which are not the OCSF numbers.
 *
 * Alerting stores `low`/`medium`/`high`/`critical` as text while the event feed stores an OCSF id.
 * They are the same scale wearing two spellings, and a screen showing both - which the case list
 * does - needs one of them to become the other rather than two color maps that can disagree.
 */
export function severityIdFromName(name: string | null | undefined): number {
  switch ((name ?? '').toLowerCase()) {
    case 'critical':
      return 5
    case 'high':
      return 4
    case 'medium':
      return 3
    case 'low':
      return 2
    case 'informational':
    case 'info':
      return 1
    default:
      return 0
  }
}
